import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { AtlassianIcon } from '@atlaskit/logo';
import { Mention } from '@atlaskit/mention';
import { Box, Inline } from '@atlaskit/primitives';
import { ProfileCardTrigger } from '@atlaskit/profilecard';
import Skeleton from '@atlaskit/skeleton';
import { useQuery } from '@atlassian/conversation-assistant-service';
import { isCustomerAgent, isForgeAgent, isSystemAgent } from '../../common/types';
import { DEFAULT_AGENTS_NAMED_ID_LIST, DEFAULT_OOTB_AGENTS_NAMED_IDS } from '../../controllers/agents/constants';
import { useProfileClient } from '../../controllers/profile-client';
import messages from './messages';
export var getAAIDFromARI = function getAAIDFromARI(ari) {
  var matched = ari.match(/\/([a-zA-Z0-9_\|\-\:]{1,128})$/);
  return matched ? matched[1] : undefined;
};
export var getARIFromAAID = function getARIFromAAID(aaid) {
  return "ari:cloud:identity::user/".concat(aaid);
};

/**
 * Renders an at-mention for the user who created this agent
 * @param agent - Agent
 * @returns JSX
 */
export var AgentCreatedBy = function AgentCreatedBy(_ref) {
  var agent = _ref.agent,
    siteId = _ref.siteId,
    simplified = _ref.simplified;
  if (isCustomerAgent(agent)) {
    var aaid = agent.creator ? getAAIDFromARI(agent.creator) : undefined;
    if (!aaid) {
      return null;
    }
    return /*#__PURE__*/React.createElement(ResolvingMention, {
      userId: aaid,
      siteId: siteId,
      profileCard: true,
      simplified: simplified
    });
  }
  if (isSystemAgent(agent) || !agent.creator) {
    return /*#__PURE__*/React.createElement(Inline, {
      alignBlock: "center",
      testId: "atlassian-icon"
    }, /*#__PURE__*/React.createElement(AtlassianIcon, {
      appearance: "brand",
      size: "small"
    }), /*#__PURE__*/React.createElement(Box, null, "Atlassian"));
  }
  if (isForgeAgent(agent)) {
    return /*#__PURE__*/React.createElement(Box, null, agent.creator);
  }
  return null;
};
var ResolvingMention = function ResolvingMention(_ref2) {
  var _profile$data, _profile$data2;
  var userId = _ref2.userId,
    siteId = _ref2.siteId,
    _ref2$profileCard = _ref2.profileCard,
    profileCard = _ref2$profileCard === void 0 ? false : _ref2$profileCard,
    _ref2$simplified = _ref2.simplified,
    simplified = _ref2$simplified === void 0 ? false : _ref2$simplified;
  var _useProfileResolution = useProfileResolution(userId, siteId),
    profile = _useProfileResolution.profile,
    client = _useProfileResolution.client;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var fullName = ((_profile$data = profile.data) === null || _profile$data === void 0 ? void 0 : _profile$data.status) === 'inactive' && profile.data.fullName ? formatMessage(messages.deactivatedCreatorName, {
    name: profile.data.fullName
  }) : (_profile$data2 = profile.data) === null || _profile$data2 === void 0 ? void 0 : _profile$data2.fullName;
  if (profile.isFetching || !profile.data) {
    return /*#__PURE__*/React.createElement(Skeleton, {
      isShimmering: true,
      height: 18,
      width: 100,
      borderRadius: 3
    });
  }
  if (simplified) {
    return /*#__PURE__*/React.createElement("a", {
      href: "/people/".concat(userId),
      target: "_blank"
    }, fullName);
  }
  return profileCard ? /*#__PURE__*/React.createElement(ProfileCardTrigger, {
    userId: userId,
    resourceClient: client,
    ariaLabel: fullName
  }, /*#__PURE__*/React.createElement(Mention, {
    id: userId,
    isHighlighted: profile.data.isCurrentUser,
    text: "@".concat(fullName)
  })) : /*#__PURE__*/React.createElement(Mention, {
    id: userId,
    isHighlighted: profile.data.isCurrentUser,
    text: "@".concat(fullName)
  });
};
export function useProfileResolution(userId, siteId) {
  var shouldFetch = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  var profileClient = useProfileClient();
  var profile = useQuery({
    queryKey: ['fetchProfile', siteId, userId],
    queryFn: function () {
      var _queryFn = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var response;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 3;
              return profileClient.getProfile(siteId, userId);
            case 3:
              response = _context.sent;
              return _context.abrupt("return", response);
            case 7:
              _context.prev = 7;
              _context.t0 = _context["catch"](0);
              return _context.abrupt("return", {});
            case 10:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[0, 7]]);
      }));
      function queryFn() {
        return _queryFn.apply(this, arguments);
      }
      return queryFn;
    }(),
    enabled: shouldFetch
  });
  return {
    profile: profile,
    client: profileClient
  };
}
export var filterAndSortAgents = function filterAndSortAgents(agents, filters) {
  return agents.filter(function (agent) {
    var _agent$description;
    return agent.name.toLocaleLowerCase().includes(filters.searchValue.toLocaleLowerCase()) || ((_agent$description = agent.description) === null || _agent$description === void 0 ? void 0 : _agent$description.toLocaleLowerCase().includes(filters.searchValue.toLocaleLowerCase()));
  }).sort(function (a, b) {
    return a.name.localeCompare(b.name);
  });
};
export var getEnabledKnowlegeArisFromAgent = function getEnabledKnowlegeArisFromAgent(agent) {
  var _agent$knowledge_sour;
  if (!((_agent$knowledge_sour = agent.knowledge_sources) !== null && _agent$knowledge_sour !== void 0 && _agent$knowledge_sour.enabled)) {
    return [];
  }
  return agent.knowledge_sources.sources.filter(function (source) {
    return source.enabled;
  }).reduce(function (acc, source) {
    var _source$configuration, _source$configuration2, _source$configuration3, _source$configuration4, _source$configuration5, _source$configuration6;
    switch (source.source) {
      case 'confluence':
        acc.push.apply(acc, _toConsumableArray((_source$configuration = (_source$configuration2 = source.configuration) === null || _source$configuration2 === void 0 ? void 0 : _source$configuration2.parent_filter) !== null && _source$configuration !== void 0 ? _source$configuration : []));
        acc.push.apply(acc, _toConsumableArray((_source$configuration3 = (_source$configuration4 = source.configuration) === null || _source$configuration4 === void 0 ? void 0 : _source$configuration4.space_filter) !== null && _source$configuration3 !== void 0 ? _source$configuration3 : []));
        break;
      case 'jira':
        acc.push.apply(acc, _toConsumableArray((_source$configuration5 = (_source$configuration6 = source.configuration) === null || _source$configuration6 === void 0 ? void 0 : _source$configuration6.project_filter) !== null && _source$configuration5 !== void 0 ? _source$configuration5 : []));
        break;
    }
    return acc;
  }, []);
};
export var getSuggestedOotbAgents = function getSuggestedOotbAgents(_ref3) {
  var _DEFAULT_OOTB_AGENTS_;
  var availableAgents = _ref3.availableAgents,
    product = _ref3.product;
  // If no product is provided / product is not supported, fallback to using default agents
  var defaultAgentsNamedIds = product ? (_DEFAULT_OOTB_AGENTS_ = DEFAULT_OOTB_AGENTS_NAMED_IDS[product]) !== null && _DEFAULT_OOTB_AGENTS_ !== void 0 ? _DEFAULT_OOTB_AGENTS_ : DEFAULT_AGENTS_NAMED_ID_LIST : DEFAULT_AGENTS_NAMED_ID_LIST;
  var defaultAgents = availableAgents.filter(function (agent) {
    return defaultAgentsNamedIds.has(agent.named_id) || agent.external_config_reference && defaultAgentsNamedIds.has(agent.external_config_reference);
  });
  return defaultAgents;
};
export var isDevelopmentAgent = function isDevelopmentAgent(agent) {
  var _agent$action_config;
  return ((_agent$action_config = agent.action_config) !== null && _agent$action_config !== void 0 ? _agent$action_config : []).some(function (config) {
    return config.id === 'agent_action_jira_issue_trigger_autodev';
  });
};